export type MergeableMessage = {
  id: string
  client_id?: string | null
  external_message_id?: string | null
  direction?: string | null
  ts: number
  body?: string | null
  status?: string | null
  attachments?: readonly unknown[] | null
  actions?: unknown
  verdict?: unknown
  unread?: unknown
}

function messageIdentityKeys(message: MergeableMessage): string[] {
  return [
    message.id,
    message.client_id,
    message.external_message_id,
  ].filter((key): key is string => typeof key === 'string' && key.length > 0)
}

function messageStatusRank(status: MergeableMessage['status']): number {
  switch (status) {
    case 'received':
    case 'replied':
      return 5
    case 'dispatched':
      return 4
    case 'queued':
      return 3
    case 'error':
      return 2
    case 'cancelled':
      return 1
    default:
      return 0
  }
}

function isFinalStatus(status: MergeableMessage['status']): boolean {
  return status === 'received' || status === 'replied'
}

function mergeMessageRecord<T extends MergeableMessage>(a: T, b: T): T {
  const aRank = messageStatusRank(a.status)
  const bRank = messageStatusRank(b.status)
  const statusChanged = a.status !== b.status
  const preferred = statusChanged
    ? (isFinalStatus(a.status) && !isFinalStatus(b.status) && b.status !== 'error' ? a : b)
    : (bRank >= aRank ? b : a)
  const other = preferred === b ? a : b
  const preferredBody = preferred.body ?? ''
  const otherBody = other.body ?? ''
  const preserveStreamingPrefix =
    a.direction === 'outbound' &&
    b.direction === 'outbound' &&
    a.status === 'dispatched' &&
    b.status === 'dispatched' &&
    otherBody.length > preferredBody.length &&
    otherBody.startsWith(preferredBody)
  const attachments = (preferred.attachments?.length ?? 0) >= (other.attachments?.length ?? 0)
    ? preferred.attachments
    : other.attachments
  return {
    ...other,
    ...preferred,
    id: preferred.id || other.id,
    client_id: preferred.client_id ?? other.client_id,
    external_message_id: preferred.external_message_id ?? other.external_message_id,
    body: preserveStreamingPrefix ? otherBody : preferredBody || otherBody,
    attachments,
    actions: preferred.actions ?? other.actions,
    verdict: preferred.verdict ?? other.verdict,
    unread: preferred.unread,
  } as T
}

export function mergeVisibleMessages<T extends MergeableMessage>(
  ...groups: Array<T[] | undefined | null>
): T[] {
  const merged: T[] = []
  const order: number[] = []
  const keyToIndex = new Map<string, number>()
  let sequence = 0

  for (const message of groups.flatMap((group) => group ?? [])) {
    const messageOrder = sequence++
    const keys = messageIdentityKeys(message)
    const index = keys
      .map((key) => keyToIndex.get(key))
      .find((value): value is number => typeof value === 'number')
    if (index === undefined) {
      const nextIndex = merged.length
      merged.push(message)
      order[nextIndex] = messageOrder
      for (const key of keys) keyToIndex.set(key, nextIndex)
      continue
    }
    merged[index] = mergeMessageRecord(merged[index]!, message)
    order[index] = messageOrder
    for (const key of messageIdentityKeys(merged[index]!)) keyToIndex.set(key, index)
  }

  return merged
    .map((message, index) => ({ message, index }))
    .sort((a, b) =>
      (a.message.ts - b.message.ts) ||
      ((order[a.index] ?? a.index) - (order[b.index] ?? b.index)),
    )
    .map((entry) => entry.message)
}
